import { useState, useEffect } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import '../../order/order-list.scss';
import { Typography } from '../../../components/typography/typography';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { DataTable, DataTableRow, DataTableColumn } from '../../../components/table/data-table';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { DateTime } from 'luxon';
import FileSaver from 'file-saver';
import { QuipliLink } from '../../../components/link/quipli-link';
import '../transactions/transaction-report.scss';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import {
  DateTimeRange,
  TaxExemptReportRequest,
  TaxExemptReportRow,
} from '../../../clients/quipli-service/report/report';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { convertToMoney } from '../../../util/util';
import { SpinnerModal } from '../../../components/modal/spinner-modal';
import { toast } from '../../../util/toasts';

export const TaxExemptReport = () => {
  const { getMultipleConfigurations } = useAPI();
  const { taxExemptReport, exportTaxExemptReport, getAvailableOrdersReportRange } = useReportApiV2();

  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [tablePagination, setTablePagination] = useState<PaginationData>();

  const [availableTaxReportRange, setAvailableTaxReportRange] = useState<DateTimeRange>();
  const [timezone, setTimezone] = useState<string>();
  const [currency, setCurrency] = useState<Dinero.Currency>();

  const [isExporting, setIsExporting] = useState<boolean>(false);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<TaxExemptReportRequest>(
    {
      from: params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined,
      to: params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined,
    },
    (_request) => {
      setIsTableDataLoading(true);

      if (!currency || !timezone || !_request.from || !_request.to) {
        return;
      }

      taxExemptReport(_request)
        .then(({ items, pagination }) => {
          setTableData(items.map((item) => buildDataRow(item)));
          setTablePagination(pagination);
          setIsTableDataLoading(false);
        })
        .catch(() => {
          setIsTableDataLoading(false);
          setTableData([]);
        });
    },
    [currency, timezone],
  );

  const columns: DataTableColumn[] = [
    { key: 'OrderNumber', label: 'Order No' },
    { key: 'CustomerName', label: 'Customer' },
    { key: 'Location', label: 'Location' },
    { key: 'RequestedTime', label: 'Requested Time' },
    { key: 'RentalStart', label: 'Rental Start' },
    { key: 'RentalEnd', label: 'Rental End' },
    { key: 'GrandTotal', label: 'Grand Total' },
  ];

  const buildDataRow = (row: TaxExemptReportRow) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {row['Customer Name']}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            <QuipliLink
              to={`orders/view/${row['Order ID']}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row['Order Number']}
            </QuipliLink>
          </Typography>
        </>
      ),
      fields: {
        OrderNumber: (
          <Typography type="text-sm" weight="regular">
            <QuipliLink
              to={`orders/view/${row['Order ID']}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row['Order Number']}
            </QuipliLink>
          </Typography>
        ),
        CustomerName: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            <QuipliLink
              to={`users/customer_form/${row['Customer ID']}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row['Customer Name']}
            </QuipliLink>
          </Typography>
        ),
        RequestedTime: (
          <div>
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {DateTime.fromISO(row['Requested Date']).toFormat('LLL-dd')}
            </Typography>
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              {row['Requested Time']}
            </Typography>
          </div>
        ),
        Location: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            {row.Location}
          </Typography>
        ),
        RentalStart: (
          <div>
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {DateTime.fromISO(row['Rental Start Date']).toFormat('LLL-dd')}
            </Typography>
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              {row['Rental Start Time']}
            </Typography>
          </div>
        ),
        RentalEnd: (
          <div>
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {DateTime.fromISO(row['Rental End Date']).toFormat('LLL-dd')}
            </Typography>
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              {row['Rental End Time']}
            </Typography>
          </div>
        ),
        GrandTotal: (
          <Typography type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
            {convertToMoney(row['Grand Total'], currency!).toFormat()}
          </Typography>
        ),
      },
    };
  };

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const file = await exportTaxExemptReport(request);

      const exportFileName = `Tax_Exempt_Report_${(request.from || DateTime.local())
        .toFormat('D')
        .replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e: any) {
      console.error(e);
      toast('Something went wrong while exporting the report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const fetchConfigs = async () => {
      const [reportRange, conf] = await Promise.all([
        getAvailableOrdersReportRange(),
        getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']),
      ]);

      const timezone = conf.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
      const currency = conf.find((c) => c.variable === 'CONF_CURRENCY')?.value;

      setAvailableTaxReportRange(reportRange);
      setTimezone(timezone);
      setCurrency(currency as Dinero.Currency);

      if (!request.from && !request.to) {
        setRequest({
          from: DateTime.local({ zone: timezone }).startOf('month'),
          to: DateTime.local({ zone: timezone }).endOf('month'),
        });
      }
    };
    fetchConfigs();
  }, []);

  const reportingMonths: DateTimeRange[] = [];
  if (availableTaxReportRange) {
    let currentStart = availableTaxReportRange.from.startOf('month');
    while (currentStart.diffNow().milliseconds < 0) {
      reportingMonths.push({ from: currentStart, to: currentStart.endOf('month') });
      currentStart = currentStart.plus({ months: 1 }).setZone(timezone, { keepLocalTime: true });
    }
  }
  reportingMonths.reverse();
  let selectedMonth = -1;
  if (request.from && request.to) {
    selectedMonth = reportingMonths.findIndex(
      (m) => m.from.diff(request.from!).milliseconds === 0 && m.to.diff(request.to!).milliseconds === 0,
    );
  }

  return (
    <div className="order-list container-v2">
      <div className="flex justify-between items-center">
        <Typography type="display-sm" weight="medium">
          Tax Exempt
        </Typography>
      </div>

      <div className="pt-10 pb-4 flex flex-col md:flex-row flex-wrap gap-4 md:items-end md:justify-between">
        <div className="flex flex-col md:flex-row flex-wrap justify-start items-end gap-4">
          <select
            className="h-fit text-[14px] w-full md:w-fit"
            value={selectedMonth}
            required={false}
            onChange={(e) => {
              const range = reportingMonths[parseInt(e.target.value)];
              setRequest({
                from: range.from,
                to: range.to,
              });
            }}
          >
            {selectedMonth < 0 && <option value={-1}></option>}
            {reportingMonths.map((m, i) => (
              <option key={i} value={i}>
                {m.from.toFormat('LLLL yyyy')}
              </option>
            ))}
          </select>
          <div className="w-full md:w-fit">
            <label className="font-medium">From</label>
            <DatePicker
              value={request.from}
              onChange={(from) => from && setRequest((prevState) => ({ ...prevState, from: from.startOf('day') }))}
            />
          </div>
          <div className="w-full md:w-fit">
            <label className="font-medium">To</label>
            <DatePicker
              value={request.to}
              onChange={(to) => to && setRequest((prevState) => ({ ...prevState, to: to.endOf('day') }))}
            />
          </div>
        </div>

        <Button className="button-yellow font-semibold h-fit" onClick={exportToCSV} data-cy="export">
          Export
          <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
        </Button>
      </div>

      <DataTable
        isLoading={isTableDataLoading}
        columnNames={columns}
        data={tableData}
        pagination={tablePagination}
        request={request}
        setRequest={setRequest}
        cypressPrevious="previous_transactions_page"
        cypressNext="next_transactions_page"
      />
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Tax Exempt Report'} />
    </div>
  );
};
